import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getApiUrl } from '../lib/config';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  actor: string | null;
  role: string | null;
  login: (password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => localStorage.getItem('pp-auth') !== null);
  const [isLoading] = useState(false);
  const [actor, setActor] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);

  const fetchAuthInfo = useCallback(async () => {
    const token = localStorage.getItem('pp-auth');
    if (!token) return;
    try {
      const res = await fetch(`${getApiUrl()}/auth/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActor(data.actor || null);
        setRole(data.role || null);
      }
    } catch {
      // Silently fail — actor/role are nice-to-have
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchAuthInfo();
  }, [isAuthenticated, fetchAuthInfo]);

  const login = useCallback(async (password: string) => {
    try {
      const res = await fetch(`${getApiUrl()}/auth/info`, {
        headers: { 'Authorization': `Bearer ${password}` }
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('pp-auth', password);
        setIsAuthenticated(true);
        setActor(data.actor || null);
        setRole(data.role || null);
        return { success: true };
      }
      return { success: false, error: 'Invalid password' };
    } catch {
      return { success: false, error: 'Connection error' };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('pp-auth');
    setIsAuthenticated(false);
    setActor(null);
    setRole(null);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, actor, role, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be within AuthProvider');
  return ctx;
};
